import {
  DEFAULT_ZOOM,
  MINIMUM_ZOOM_FOR_SELECTING_LOCATION,
  getDefaultLocationForUser,
  getMapCenter,
  setMapCenter,
} from "../../v2_actions/map";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
  useJsApiLoader,
} from "@react-google-maps/api";
import React, { useEffect, useRef, useState } from "react";
import { get, head, map, size } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { AddressSearch } from "./AddressSearch";
import { getGoogleInitArgs } from "../../v2_actions/map";

const containerStyle = {
  width: "100%",
  height: "515px",
  overflow: "hidden",
  padding: 0,
};

export const Map = React.forwardRef(
  (
    {
      markers,
      pins,
      initialZoom,
      onZoomChanged,
      onSelectPlace,
      onSelectPosition,
      onCancel,
      renderOverlay,
      googleMapProps,
      selectLocation = false,
      currentLocation,
      setSelectedLocation
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const [mapTypeId, setMapTypeId] = useState("roadmap");
    const [hoveredMarker, setHoveredMarker] = useState(null);
    const placesSearchRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [localCenter, setLocalCenter] = useState(getDefaultLocationForUser());
    const [zoom, setZoom] = useState(initialZoom || DEFAULT_ZOOM);
    const reduxCenter = useSelector((state) => getMapCenter(state));
    const isLoaded =
      get(useJsApiLoader(getGoogleInitArgs()), "isLoaded") &&
      zoom &&
      parseFloat(get(localCenter, "lat"));

    useEffect(() => {
      if (get(reduxCenter, "lat") && get(reduxCenter, "lng")) {
        setLocalCenter(reduxCenter);
      }
      
    }, [reduxCenter]);

    useEffect(() => {
      if (currentLocation?.lat && currentLocation?.lng) {
        setLocalCenter(currentLocation);
      }
    }, [currentLocation]);

    const handleZoomChanged = () => {
      if (!mapInstance) {
        return;
      }
      setZoom(mapInstance.zoom);
      if (onZoomChanged) {
        onZoomChanged(zoom);
      }
    };

    const handleMapTypeIdChanged = () => {
      if (get(mapInstance, "mapTypeId")) {
        setMapTypeId(mapInstance.mapTypeId);
      }
    };

    const handlePlacesChanged = (places) => {
      const loc = get(head(places), ["geometry", "location"]);
      const lat = loc.lat();
      const lng = loc.lng();
      if (onSelectPlace) {
        onSelectPlace({ lat, lng });
      }
      dispatch(setMapCenter({ lat, lng }));
      setZoom(MINIMUM_ZOOM_FOR_SELECTING_LOCATION);
    };

    const handleMark = (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      if (onSelectPosition) {
        onSelectPosition({ lat, lng });
      }
      setLocalCenter({ lat, lng });
      setSelectedLocation({ lat, lng });

    };

    const onMapLoaded = React.useCallback((mapInstance) => {
      setMapInstance(mapInstance);
    });

    const renderMarkers = () => {
      return map(markers, (marker, index) => {
        const { imageUrl, name, position, onClick } = marker;
        const isHovered = marker === hoveredMarker;

        if (!name) {
          return <Marker position={position} key={index} />;
        } else {
          return (
            <>
              <Marker
                key={index}
                position={position}
                icon={{
                  url: imageUrl,
                  labelOrigin: new window.google.maps.Point(30, -50),
                }}
                zIndex={isHovered ? 9999 : 0}
                labelAnchor={{ x: 30, y: 50 }}
                onMouseOver={() => setHoveredMarker(marker)}
                onMouseOut={() => setHoveredMarker(null)}
                onClick={onClick}
              />

              {isHovered && (
                <InfoWindow
                  position={position}
                  onCloseClick={() => setHoveredMarker(null)}
                >
                  <div>
                    <p>{name}</p>
                  </div>
                </InfoWindow>
              )}
            </>
          );
        }
      });
    };

    return (
      <>
        {renderOverlay && (
          <div
            style={{
              top: "10%",
              margin: "auto",
              width: "100%",
              position: "absolute",
              display: "flex",
              alignItems: "center",
              zIndex: "999",
              justifyContent: "center",
            }}
            onMouseLeave={() => onCancel}
          >
            {renderOverlay()}
          </div>
        )}

        {!isLoaded && <div>Loading...</div>}

        {isLoaded && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            onLoad={onMapLoaded}
            zoom={zoom}
            center={localCenter}
            onClick={handleMark}
            onZoomChanged={handleZoomChanged}
            onMapTypeIdChanged={handleMapTypeIdChanged}
            options={{
              streetViewControl: false,
              mapTypeId: mapTypeId,
              fullscreenControl: false,
            }}
            {...googleMapProps}
          >
              <AddressSearch onChanged={handlePlacesChanged} map />

            {size(markers) > 0 && (
              <MarkerClusterer
                averageCenter
                calculator={markerClustererCalculator}
                enableRetinaIcons
                gridSize={30}
              >
                {renderMarkers}
              </MarkerClusterer>
            )}
            {map(pins, (pin) => {
              return (
                pin.lat && <Marker position={{ lat: pin.lat, lng: pin.lng }}/>
              )
          })}
          </GoogleMap>
        )}
      </>
    );
  },
);

const markerClustererCalculator = (markers) => {
  return {
    index: 1,
    text: size(markers),
  };
};
